// @flow
// $FlowIssue need to update to a more recent flow version
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import { Route } from 'react-router';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Link } from '@material-ui/core';
import { KeyboardArrowLeft as KeyboardArrowLeftIcon } from '@material-ui/icons';
import type { $AxiosError } from 'axios';
import type { Dispatch } from 'redux';
import type { Connector } from 'react-redux';
import type { ContextRouter } from 'react-router';
import ApplyDialog from '../ApplyDialog';
import ReviewDialog from '../ReviewDialog';
import TicketInfoDialog from '../TicketInfoDialog';
import WithdrawDialog from '../WithdrawDialog';
import ExtendReservationWindowDialog from '../ExtendReservationWindowDialog';
import LoadingPage from '../../components/LoadingPage';
import SimplePane from '../../components/SimplePane';
import Summary from './containers/Summary';
import Description from './containers/Description';
import Comments from './containers/Comments';
import Instructions from '../../../browser/tickets/view/info/Instructions';
import { format } from '../../../browser/shared/util/gigwalkApiErrorUtil';
import * as snackbar from '../../ducks/snackbar';
import { actions } from './duck';
import styles from './styles';

type OwnProps = ContextRouter & {};
type StateProps = {};
type DispatchProps = {
    enqueueSnackbar: (message: string, options: Object) => void,
    loadTicket: (ticketId: number) => Promise<void>,
};
type Props = OwnProps & StateProps & DispatchProps;

const useStyles = makeStyles(styles, { name: 'TicketDetail' });

export function TicketDetail(props: Props) {
    const { enqueueSnackbar, history, loadTicket, match } = props;
    const ticketId = parseInt(match.params.ticketId, 10);
    const orgId = parseInt(match.params.orgId, 10);

    const classes = useStyles(props);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);

    const gridRef = useRef(null);
    const resizeObserverRef = useRef(null);

    const resizeGrid = useCallback(() => {
        const grid = gridRef.current;

        if (grid instanceof HTMLElement) {
            let leftHeight = 0;
            let rightHeight = 0;

            for (let i = 0; i < grid.children.length; i += 1) {
                const item = grid.children[i];
                const { height } = item.firstElementChild.getBoundingClientRect();

                if (item instanceof HTMLElement) {
                    if (item.classList.contains(classes.leftColumn)) {
                        item.style.top = `${leftHeight}px`; // eslint-disable-line no-param-reassign
                        leftHeight += height;
                    } else {
                        item.style.top = `${rightHeight}px`; // eslint-disable-line no-param-reassign
                        rightHeight += height;
                    }
                }
            }

            grid.style.height = `${Math.max(leftHeight, rightHeight)}px`;
        }
    }, [classes.leftColumn]);

    const goBack = useCallback(() => {
        const historyState = window.history.state;
        if (historyState && historyState.breadcrumb) {
            history.goBack();
        } else {
            history.push(`/tickets/${orgId}/list`);
        }
    }, [history, orgId]);

    useEffect(() => {
        setLoading(true);
        loadTicket(ticketId)
            .then(() => setLoading(false))
            .catch((err: $AxiosError<any>) => {
                const resp = err ? err.response : null;
                if (resp && resp.data && resp.data.gw_api_response) {
                    const message = format(resp.data.gw_api_response);
                    enqueueSnackbar(message, { variant: 'error' });
                }

                // @todo Handle permission errors in a more general way across the app
                if (resp && (resp.status === 404 || resp.status === 403)) {
                    // Redirect to ticket list after a short delay to avoid weirdness caused
                    // by navigator and AppStateUrlSyncer
                    setTimeout(() => goBack(), 1000);
                }
            });
    }, [enqueueSnackbar, goBack, loadTicket, ticketId]);

    useEffect(() => {
        if (!loading) {
            resizeGrid();
        }
    }, [loading, resizeGrid]);

    useEffect(() => {
        const grid = gridRef.current;
        let resizeObserver = resizeObserverRef.current;

        if (!loading && !resizeObserver && grid instanceof HTMLElement) {
            resizeObserver = new ResizeObserver(() => {
                resizeGrid();
            });

            for (let i = 0; i < grid.children.length; i += 1) {
                const item = grid.children[i];
                resizeObserver.observe(item.firstElementChild);
            }

            resizeObserverRef.current = resizeObserver;
        }

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserverRef.current = null;
            }
        };
    }, [loading, resizeGrid]);

    if (loading) {
        return (
            <SimplePane>
                <LoadingPage />
            </SimplePane>
        );
    }

    return (
        <SimplePane>
            <Link className={classes.link} onClick={goBack} underline="none" variant="body2">
                <KeyboardArrowLeftIcon fontSize="small" />
                {t('ticketDetail.breadcrumbText')}
            </Link>
            <div className={classes.grid} ref={gridRef}>
                <div className={cx(classes.item, classes.leftColumn)}>
                    <div className={classes.contentWrapper}>
                        <Route component={Summary} />
                    </div>
                </div>
                <div className={cx(classes.item, classes.leftColumn)}>
                    <div className={classes.contentWrapper}>
                        <Route component={Instructions} />
                    </div>
                </div>
                <div className={cx(classes.item, classes.rightColumn)}>
                    <div className={classes.contentWrapper}>
                        <Route component={Description} />
                    </div>
                </div>
                <div className={cx(classes.item, classes.rightColumn)}>
                    <div className={classes.contentWrapper}>
                        <Route component={Comments} />
                    </div>
                </div>
            </div>
            <ReviewDialog />
            <TicketInfoDialog />
            <ApplyDialog />
            <WithdrawDialog />
            <ExtendReservationWindowDialog />
        </SimplePane>
    );
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
    enqueueSnackbar: (message: string, options: Object) => dispatch(snackbar.actions.enqueue(message, options)),
    loadTicket: (ticketId: number) => dispatch(actions.loadTicket(ticketId)),
});

const connector: Connector<OwnProps, Props> = connect(null, mapDispatchToProps);
export default connector(TicketDetail);
